import dateColumn from "../../../../layouts/Table/columns/dateColumn";
import { useLanguage } from "../../../../stores/LanguageStore";
import { useCallback, useMemo, useState } from "react";
import { Divider, Flex, Group, Stack, Text } from "@mantine/core";
import MainTable from "../../../../layouts/Table/MainTable";
import { IconChevronDown } from "@tabler/icons-react";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useNavigate, useParams } from "react-router-dom";
import SelectionDeleter from "../../../../components/TableComponents/SelectionDeleter";
import { useListingLoader } from "../../../../components/hooks/ListingHooks";
import numberColumn from "../../../../layouts/Table/columns/numberColumn";
import { useListStore } from "../../../../stores/ListStore";
import { getRowClasses } from "../../lists/ListingWithContent/listing";
import { CommCode } from "../../community/CommCode";
import {
  deleteOrg,
  deleteTeam,
  editOrg,
  getProfile,
} from "../../../../api/UserAPI";
import { sortDescId } from "../../../../functions/utilFunctions";
import ItemDeleter from "../../../../components/TableComponents/ItemDeleter";
import InviteToCommunity from "../../community/InviteToCommunity";
import CreateOrgButton from "../../community/orgs/CreateOrgButton";
import NoItemsModal from "../../../../components/TableComponents/NoItemsModal";
import { nameColumn } from "../../../../layouts/Table/columns/nameColumn";
import LeaveCommunity from "../../community/LeaveCommunity";

const OrgListing = () => {
  const { orgId } = useParams();
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);
  const org_id = useMemo(() => parseInt(orgId) || null, [orgId]);

  const columns = useMemo(() => {
    return [
      numberColumn(),
      nameColumn({
        editFunction: (row) => editOrg,
        apiRef,
        width: 200,
        editorStyle: { right: 16 },
        handleClick: () => {},
        disableLink: true,
      }),
      numberColumn(
        "number_of_users",
        lang.resellers.columns.number_of_users,
        120
      ),
      {
        field: "organization_code",
        headerName: lang.organizations.columns.organization_code,
        width: 175,
        renderCell: ({ value }) => {
          return <CommCode token={value} />;
        },
      },
      dateColumn(),
    ];
  }, [lang]);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [highlight, setHighlight] = useState(null);

  const navigate = useNavigate();
  const handleRowClick = ({ row }) => {
    const setMinimized = useListStore.getState().setModelMinimized;

    if (org_id === row.id) {
      navigate(`/settings/organizations/none`);
    } else {
      navigate(`/settings/organizations/${row.id}`);
      setMinimized(true);
    }
  };

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          {org_id ? (
            ""
          ) : (
            <>
              <InviteToCommunity current={row} isAction isOrg />
              <LeaveCommunity
                current={row}
                isOrg={true}
                setTableLoading={setLoading}
              />
              <ItemDeleter
                title_bold={`${row.name} #${row.id}`}
                handleDelete={deleteOrg}
                deletedMessage={lang.organizations.delete_success}
                id={row.id}
                apiRef={apiRef}
              />
            </>
          )}

          <IconChevronDown
            size={24}
            className="bump"
            opacity={org_id === row.id ? 1 : 0.4}
            style={{
              transform: org_id === row.id ? "rotate(-90deg)" : "none",
              transition: "all ease .3s",
            }}
            onClick={() => handleRowClick({ row })}
          />
        </Group>
      );
    },
    [org_id]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <>
        <SelectionDeleter
          handleDelete={deleteOrg}
          apiRef={apiRef}
          setTableLoading={setLoading}
          ids={selectedIds}
        />
      </>
    ),
    [apiRef]
  );

  // useEffect(() => {
  //   if (rows.length === 1) {
  //     const setMinimized = useListStore.getState().setModelMinimized;
  //     setMinimized(true);
  //     navigate("/settings/organizations/" + rows[0].id + "/users?only_org=true");
  //   }
  // }, [rows]);

  useListingLoader({
    loading,
    setLoading,
    listGetter: getProfile,
    responseField: "user_profile",
    setRows,
    getRows: (data) => sortDescId(data.organizations),
  });

  return (
    <Stack gap={0} pr={48} pt={16} h={"calc(100vh - 96px)"}>
      <Group gap={"md"}>
        <Text size="xl" fw={600} mr={"auto"}>
          {lang.settings.org_title}
        </Text>

        <CreateOrgButton setTableLoading={setLoading} />
      </Group>

      <Divider my={16} />

      <Flex flex={1} pos={"relative"}>
        <NoItemsModal
          rows={rows}
          loading={loading}
          text={
            <Text size="xl" mb={32}>
              {lang.settings.no_org}
            </Text>
          }
          button={<CreateOrgButton />}
        />
        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          loading={loading}
          setLoading={setLoading}
          id={"model-table"}
          toolbarActions={org_id ? null : toolbarActions}
          actionsCell={actionsCell}
          onRowClick={handleRowClick}
          hasFolder
          // pageOrderName={"teams_order"}
          defaultHighlight={highlight}
          setDefaultHighlight={setHighlight}
          whiteButtons={!org_id}
          actionsCellWidth={org_id ? 75 : 128}
          disableGotoPage={org_id}
          disableVisibleColumns={org_id}
          defaultPerPage={15}
          disablePerPage={org_id}
          disableCheckboxes={!!org_id}
          getRowClassName={(params) =>
            getRowClasses({ params, mainId: orgId, integerId: org_id })
          }
          withSorter={!!org_id}
          tableSx={{
            "& .MuiDataGrid-columnHeaders": {
              border: "none !important",
              display: org_id ? "none" : "flex",
            },
            "& .MuiDataGrid-cell": {
              border: "none",
            },
            "& .MuiDataGrid-main": {
              background: "var(--mantine-color-primary-2)",
              border: "none !important",
              borderRadius: 3,
            },
            "& .table-search": {
              width: org_id ? "200px !important" : "300px !important",
            },
            "& .MuiDataGrid-footerContainer": {
              border: "none !important",
            },
            "& .table-row": {
              cursor: "pointer",
              borderRadius: 3,
              marginBottom: 1,
              background: "#fff",
              border: "1px solid var(--mantine-color-primary-1)",
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              background: "transparent",
              borderRight: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--right": {
              background: "var(--mantine-color-primary-2) !important",
              border: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--left": {
              background: "var(--mantine-color-primary-2) !important",
              border: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--right .table-row": {
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              background: "#fff",
              border: "1px solid   var(--mantine-color-primary-1)",
              borderLeft: "none !important",
            },
            "& .MuiDataGrid-pinnedColumns--left .table-row": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              background: "#fff",
              border: "1px solid   var(--mantine-color-primary-1)",
              borderRight: "none !important",
            },
            "& .MuiDataGrid-virtualScrollerRenderZone .table-row": {
              borderRadius: org_id ? 3 : 0,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
            "& .MuiDataGrid-selectedRowCount": {
              visibility: org_id ? "hidden" : "visible",
            },
            "& .MuiPagination-root": {
              left: org_id ? 0 : undefined,
              transform: org_id ? "none" : undefined,
            },
          }}
        />
      </Flex>
    </Stack>
  );
};

export default OrgListing;
